/**
 * analytics · lightweight client-side product analytics.
 *
 * Goals:
 * - Zero-dependency. No external SDK, no PII, no fingerprinting.
 * - No-op in dev so local clicks don't pollute prod data.
 * - Buffers events and flushes via sendBeacon on pagehide/visibilitychange.
 * - Respects Do Not Track and the EU "no consent, no tracking" expectation
 *   by not setting any persistent identifier · we use a per-tab session id
 *   stored only in sessionStorage.
 *
 * Events are intentionally minimal: an event name, optional flat props, and
 * an auto-attached page path. If the backend `analytics-ingest` edge function
 * is missing, events are dropped silently · this layer must never crash the app.
 */

type Props = Record<string, string | number | boolean | null | undefined>;

const ENDPOINT = "/functions/v1/analytics-ingest";
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BUFFER = 25;

let buffer: Array<{ name: string; props: Props; ts: number; path: string }> = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialised = false;

function isProd(): boolean {
  return import.meta.env.PROD === true;
}

function isDntEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  // 1 = user opted out
  return navigator.doNotTrack === "1" || (navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack === "1";
}

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const KEY = "cr_sess";
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

async function flush(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];

  const payload = {
    session_id: getSessionId(),
    events,
    sent_at: Date.now(),
    user_agent: navigator.userAgent.slice(0, 200),
  };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!supabaseUrl) return;
  const url = `${supabaseUrl}${ENDPOINT}`;

  try {
    if (useBeacon && "sendBeacon" in navigator) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Swallow · analytics must never throw into the app.
  }
}

function ensureInit(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;

  flushTimer = setInterval(() => void flush(false), FLUSH_INTERVAL_MS);

  const onLeave = () => void flush(true);
  window.addEventListener("pagehide", onLeave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onLeave();
  });
}

/**
 * Fire-and-forget event tracker. Safe to call from anywhere.
 *
 * @example
 * track("cta_clicked", { cta: "book_demo", section: "hero" });
 */
export function track(name: string, props: Props = {}): void {
  if (!isProd() || isDntEnabled()) return;
  ensureInit();

  buffer.push({
    name,
    props,
    ts: Date.now(),
    path: typeof location !== "undefined" ? location.pathname : "",
  });

  if (buffer.length >= MAX_BUFFER) void flush(false);
}

/** Force-flush · useful in tests or before a hard navigation. */
export function flushAnalytics(): Promise<void> {
  return flush(false);
}

/** For tests · resets module state. */
export function _resetAnalyticsForTests(): void {
  buffer = [];
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  initialised = false;
}
